/** @graphql agg **/
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql, useFragment } from 'react-relay';

import { expVal } from '@confluence/feature-experiments';
import { cfetch, BadStatusError } from '@confluence/network';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useFetchSuggestionCountQuery$data,
	useFetchSuggestionCountQuery$variables,
} from './__generated__/useFetchSuggestionCountQuery.graphql.ts';
import type {
	useFetchSuggestionCount_query$data,
	useFetchSuggestionCount_query$key,
} from './__generated__/useFetchSuggestionCount_query.graphql.ts';

export type RemoteObjectSuggestionCountFragmentRef = useFetchSuggestionCount_query$key;

export const RemoteObjectSuggestionCountFragment: GraphQLTaggedNode = graphql`
	fragment useFetchSuggestionCount_query on Query {
		confluence_remoteObjectSuggestionCount(cloudId: $cloudId, spaceKey: $spaceKey)
	}
`;

export const RemoteObjectSuggestionCountQuery: GraphQLTaggedNode = graphql`
	query useFetchSuggestionCountQuery($cloudId: ID!, $spaceKey: String!) {
		...useFetchSuggestionCount_query @relay(mask: false)
	}
`;

export type UseFetchSuggestionCountResult = {
	count: number;
	isLoading: boolean;
	isSuppressed: boolean;
	refetch: () => void;
};

type UseFetchSuggestionCountProps = {
	spaceKey?: string;
	cloudId: string;
};

export const useFetchSuggestionCount = ({
	spaceKey,
	cloudId,
}: UseFetchSuggestionCountProps): UseFetchSuggestionCountResult =>
	useFetchSuggestionCountInternal(
		{ spaceKey, cloudId },
		expVal('confluence_autonomous_space_curation_ssr_streaming', 'isEnabled', false),
	);

export const useFetchSuggestionCountFromFragment = ({
	spaceKey,
	cloudId,
	suggestionCountFragmentRef,
}: UseFetchSuggestionCountProps & {
	suggestionCountFragmentRef: RemoteObjectSuggestionCountFragmentRef;
}): UseFetchSuggestionCountResult => {
	const fragmentData = useFragment(RemoteObjectSuggestionCountFragment, suggestionCountFragmentRef);

	return useFetchSuggestionCountInternal(
		{ spaceKey, cloudId },
		expVal('confluence_autonomous_space_curation_ssr_streaming', 'isEnabled', false),
		fragmentData,
	);
};

const useFetchSuggestionCountInternal = (
	{ spaceKey, cloudId }: UseFetchSuggestionCountProps,
	isStreamingEnabled: boolean,
	fragmentData?: useFetchSuggestionCount_query$data,
): UseFetchSuggestionCountResult => {
	const usesRouteData = fragmentData !== undefined;

	// Apollo path — runs when experiment is on. useQueryInterop must be called
	// unconditionally; the skip option prevents network activity when unused.
	const {
		data: apolloData,
		loading: apolloLoading,
		refetch: apolloRefetch,
	} = useQueryInterop<useFetchSuggestionCountQuery$data, useFetchSuggestionCountQuery$variables>(
		RemoteObjectSuggestionCountQuery,
		{
			variables: { cloudId, spaceKey: spaceKey ?? '' },
			skip: !isStreamingEnabled || !spaceKey || usesRouteData,
			interopApolloAggClient: true,
		},
	);

	const apolloResult = useMemo<UseFetchSuggestionCountResult | null>(() => {
		if (!isStreamingEnabled) {
			return null;
		}
		const hasChildQueryData = apolloData != null;
		const raw = hasChildQueryData
			? apolloData.confluence_remoteObjectSuggestionCount
			: fragmentData?.confluence_remoteObjectSuggestionCount;
		const isLoading = usesRouteData && !hasChildQueryData ? false : apolloLoading;
		// null data is the suppression signal — this includes the case where the
		// backend returns errors alongside null data (e.g. NotFoundException when
		// the user has been permanently suppressed). Do not gate on apolloError.
		const apolloIsSuppressed = !isLoading && raw == null;
		return {
			count: raw ?? 0,
			isLoading,
			isSuppressed: apolloIsSuppressed,
			refetch: () => {
				void apolloRefetch();
			},
		};
	}, [apolloData, apolloLoading, apolloRefetch, fragmentData, isStreamingEnabled, usesRouteData]);

	// cfetch path — used when experiment is off
	const [count, setCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [isSuppressed, setIsSuppressed] = useState(false);

	const fetchCount = useCallback(async () => {
		if (isStreamingEnabled) {
			return;
		}

		if (!spaceKey) {
			setCount(0);
			setIsLoading(false);
			return;
		}

		setIsLoading(true);

		try {
			const response = await cfetch(
				`/wiki/rest/internal/1.0/spaces/${spaceKey}/remote-objects/suggestions/count`,
				{ method: 'GET' },
			);

			const text = await response.text();
			const parsed = parseInt(text, 10);
			if (isNaN(parsed)) {
				setIsSuppressed(true);
			} else {
				setCount(parsed);
				setIsSuppressed(false);
			}
		} catch (error) {
			if (BadStatusError.isBadStatusError(error) && error.response.status === 404) {
				setIsSuppressed(true);
			}
			// silently fail on other errors
		} finally {
			setIsLoading(false);
		}
	}, [isStreamingEnabled, spaceKey]);

	useEffect(() => {
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
		void fetchCount();
	}, [fetchCount]);

	if (isStreamingEnabled) {
		return apolloResult ?? { count, isLoading, isSuppressed, refetch: fetchCount };
	}

	return { count, isLoading, isSuppressed, refetch: fetchCount };
};
